/* eslint import/no-named-as-default-member: 0 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translation, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  DataTable,
  DataTablePagination,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@cargomatic/ui';
import {
  COMPLETE_STOP,
  PROVIDE_POD,
  ADD_CONTAINER,
  CONFIRM_APPT,
  ENTER_REQUIRED_DATES,
  SCHEDULE_APPT,
  PROVIDE_ARRIVAL_INFORMATION,
  ADD_DOCUMENTS,
} from 'constants/next-actions';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import {
  getShipperStopDocumentRequirementsFromConfig,
  getUniqueCombinedStopDocumentRequirementsFromConfig,
} from 'helpers/stops';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import MinimumDocumentRequirements from 'components/modules/shipments/MinimumDocumentRequirements';
import Alert from 'components/ui/Alert';
import StopCompleteModal from '../shipments/stops/StopCompleteModal';
import StopEquipmentModal from '../shipments/stops/StopEquipmentModal';
import AddAppointmentModal from '../../ui/tailwind/modals/AddAppointmentModal';
import ShipmentWebAPIUtils, {
  completeStop,
} from '../../../utils/ShipmentWebAPIUtils';
import StopArrivedModal from '../shipments/stops/StopArrivedModal';
import ShipmentsActionCreators from '../../../actions/ShipmentsActionCreators';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';
import { getCarrierDashboardColumns } from './CarrierDashboardColumns';

export default function NextActionsList({
  actions,
  actionType,
  isCustomerView,
  perPage,
  page,
  pages,
  total,
  loading,
  onPageClick,
  onPerPageChange,
  setIsModalOpen,
  onShipmentClosed,
  selectedAction,
  setSelectedAction,
  setShipmentUpdatedOnModalClose,
}) {
  const history = useHistory();
  const user = SessionStore.getUser();
  const { t } = useTranslation();
  const setRedirect = (path) => history.push(path);

  const [shipperStopDocumentRequirements, setShipperStopDocumentRequirements] =
    useState({});

  const [pagination, setPagination] = useState({
    pageIndex: page - 1,
    pageSize: perPage,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page - 1,
      pageSize: perPage,
    }));
  }, [page, perPage]);

  useEffect(() => {
    if (
      selectedAction?.shipment &&
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      isIntermodalShipmentType(selectedAction?.shipment) &&
      selectedAction?.shipment?.shipper
    ) {
      const getShipperStopDocRequirements = async () => {
        const { shipper } = selectedAction.shipment;
        const shipperId = shipper?._id || shipper;

        const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(
          CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS,
        );
        const lookup = isCarrierMandatoryStopDocumentsEnabled
          ? await getUniqueCombinedStopDocumentRequirementsFromConfig(shipperId)
          : await getShipperStopDocumentRequirementsFromConfig(shipperId);

        setShipperStopDocumentRequirements(lookup);
      };
      getShipperStopDocRequirements();
    }
  }, [selectedAction]);

  const handleNextActionClick = useCallback(
    (action) => {
      setIsModalOpen(true);
      setSelectedAction(action);
      handlePageClickRedirect(action);
    },
    [setIsModalOpen, setSelectedAction],
  );

  function handleCloseNextActionModal() {
    setIsModalOpen(false);
    setSelectedAction(null);
  }

  function handlePageClickRedirect({ action, shipment }) {
    if (action === ENTER_REQUIRED_DATES) {
      const link =
        user._type === 'Admin'
          ? `/shipments/${shipment._id}`
          : `/shipments/${shipment._id}?action=set_dates`;
      setRedirect(link);
    }
  }

  async function handleStopComplete(shipment, stop, file, meta) {
    try {
      const response = await completeStop(shipment, stop, file, meta);
      if (
        stop.action === 'deliver' &&
        response.entity.job.status === 'job_closed'
      ) {
        onShipmentClosed(shipment);
      }
      handleCloseNextActionModal();
      setShipmentUpdatedOnModalClose(true);
      showToast(t(COMPLETE_STOP), t('COMPLETE_STOP_SUCCESS'));
    } catch (e) {
      console.error(e);
      showToast(t(COMPLETE_STOP), t('COMPLETE_STOP_FAIL'), 'error');
    }
  }

  function handleStopEquipmentUpdateSuccess() {
    if (selectedAction.action === ADD_CONTAINER) {
      handleCloseNextActionModal();
      setShipmentUpdatedOnModalClose(true);
    }
    showToast(t(COMPLETE_STOP), t('ADD_CONTAINER_SUCCESS'));
  }

  function handleStopEquipmentUpdateFail(e) {
    console.error(e);
    showToast(t(COMPLETE_STOP), t('ADD_CONTAINER_FAIL'), 'error');
  }

  async function handleScheduleAppt(updates) {
    try {
      const response =
        await ShipmentWebAPIUtils.updateShipmentStopSchedulingResponse(
          selectedAction.shipment,
          selectedAction.nextStop,
          updates,
        );
      if (response.entity.success) {
        showToast(t(PROVIDE_POD), t('SCHEDULE_APPT_SUCCESS'));
        handleCloseNextActionModal();
        setShipmentUpdatedOnModalClose(true);
      } else {
        throw new Error(t('ERROR_UPDATING_APPOINTMENT'));
      }
    } catch (e) {
      console.error(e);
      showToast(t('SCHEDULE_APPOINTMENT'), t('SCHEDULE_APPT_FAIL'), 'error');
    }
  }

  function renderWarningForAccessorialDocumentsConitionally(shipment) {
    if (
      shipment.accessorial?.some(
        (acc) => acc.missing_docs === true && !FEATURE_FLAG_ENABLED_BREADCRUMB,
      )
    ) {
      return (
        <Alert type="warning" className="m-4">
          <Translation>
            {(t) => (
              <>
                {t(
                  'ACCESSORIAL_DOCUMENTS_MUST_BE_UPLOADED_WITHIN_48_HOURS_OF_DELIVERY_OR_WILL_BE_AUTO-DECLINED',
                )}
              </>
            )}
          </Translation>
        </Alert>
      );
    }
    return null;
  }

  function renderActionModal() {
    if (!selectedAction) {
      return null;
    }

    if (
      selectedAction.action === SCHEDULE_APPT ||
      selectedAction.action === CONFIRM_APPT
    ) {
      return (
        <AddAppointmentModal
          stop={selectedAction.nextStop}
          shipment={selectedAction.shipment}
          show={!!selectedAction}
          onSave={handleScheduleAppt}
          onHide={handleCloseNextActionModal}
        />
      );
    }

    if (selectedAction.action === ENTER_REQUIRED_DATES) {
      return null;
    }

    if (selectedAction.action === ADD_CONTAINER) {
      return (
        <StopEquipmentModal
          show={!!selectedAction}
          shipmentId={selectedAction.shipment._id}
          stopId={selectedAction.nextStop._id}
          onStopEquipmentUpdateSuccess={handleStopEquipmentUpdateSuccess}
          onStopEquipmentUpdateFail={handleStopEquipmentUpdateFail}
          onHide={handleCloseNextActionModal}
          isCarrier={isCustomerView}
        />
      );
    }

    if (selectedAction.action === PROVIDE_ARRIVAL_INFORMATION) {
      return (
        <StopArrivedModal
          shipmentId={selectedAction.shipment._id}
          stopId={selectedAction.nextStop._id}
          show={!!selectedAction}
          onHide={handleCloseNextActionModal}
          onStopArrived={ShipmentsActionCreators.transitionStop}
        />
      );
    }
    const isDrayageDocumentRequirements =
      selectedAction.action === ADD_DOCUMENTS &&
      selectedAction?.shipment?.shipment_type !== 'shorthaul';
    if (isDrayageDocumentRequirements) {
      return (
        <Dialog
          open={!!selectedAction}
          onOpenChange={(open) => {
            if (!open) handleCloseNextActionModal();
          }}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('PROVIDE_SHIPMENT_DOCUMENTS')}</DialogTitle>
            </DialogHeader>
            {renderWarningForAccessorialDocumentsConitionally(
              selectedAction.shipment,
            )}
            <MinimumDocumentRequirements
              shipment={selectedAction.shipment}
              title="PLEASE_PROVIDE_THE_FOLLOWING_DOCUMENTS"
            />
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <StopCompleteModal
        show={!!selectedAction}
        shipmentId={selectedAction.shipment._id}
        stopId={selectedAction.nextStop._id}
        onStopComplete={handleStopComplete}
        onHide={handleCloseNextActionModal}
        onStopEquipmentUpdateSuccess={handleStopEquipmentUpdateSuccess}
        onStopEquipmentUpdateFail={handleStopEquipmentUpdateFail}
        isCarrier={isCustomerView}
        shipperStopDocumentRequirements={shipperStopDocumentRequirements}
      />
    );
  }

  const columns = useMemo(
    () =>
      getCarrierDashboardColumns({
        actionType,
        isCustomerView,
        onEdit: handleNextActionClick,
      }),
    [actionType, isCustomerView, handleNextActionClick],
  );

  const table = useReactTable({
    data: actions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    rowCount: total,
    state: { pagination },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(next);
      if (next.pageSize !== perPage) {
        onPerPageChange?.(next.pageSize);
      } else if (next.pageIndex !== page - 1) {
        onPageClick(next.pageIndex + 1);
      }
    },
  });

  return (
    <div>
      <DataTable table={table} isLoading={loading} />
      {!loading && total > 0 && pages > 1 && (
        <DataTablePagination table={table} />
      )}
      {renderActionModal()}
    </div>
  );
}

NextActionsList.propTypes = {
  actions: PropTypes.array,
  actionType: PropTypes.string,
  isCustomerView: PropTypes.bool,
  page: PropTypes.number,
  pages: PropTypes.number,
  perPage: PropTypes.number,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onPageClick: PropTypes.func,
  onPerPageChange: PropTypes.func,
  setIsModalOpen: PropTypes.func,
  onShipmentClosed: PropTypes.func,
  selectedAction: PropTypes.object,
  setSelectedAction: PropTypes.func,
  setShipmentUpdatedOnModalClose: PropTypes.func,
};

NextActionsList.defaultProps = {
  isCustomerView: true,
  page: 1,
  pages: 1,
  perPage: 10,
  loading: false,
};
